// src/Pages/Blog/posts/posting-frequency-2025.js
//
// Rewritten 2026-10-17 (evergreen pass):
//   - Invented revenue model removed ($675 / $4,860 / $19,319 / $53,978 / $52,000 had
//     no basis; they were built on unverified follower, reach and growth inputs).
//   - The old headline advice (5–7 posts a week) contradicted its own main source
//     (Buffer: 3–5 a week is the sweet spot). The answer now follows the data.
//   - Every statistic verified against the primary source; unverifiable or
//     secondary-aggregator claims removed (Business Dasher 93.79%/41%, Databox 2,240
//     followers, Socialinsider/Social Status reach rates, DashThis growth rates,
//     Hootsuite/Sprout/Synup "20–40% more revenue", WiFi Talents 51%, Nielsen 92%,
//     McKinsey 2x, QuickBooks 120 hours, Chamber 50–60 hours, Hootsuite "6–7 posts").
//   - Infographic removed and ogImage switched to the hero: the infographic carried
//     the wrong alt text and old numbers. Regenerate before re-adding.
//   - Platform list limited to platforms with real data, plus Google Business Profile.
//   - CTA is signup. Author is named. Links to the cost and matrix posts. Slug unchanged.

const post = {
    id: 2,
    slug: 'how-often-small-business-post-social-media',
    title: 'How Often Should a Small Business Post on Social Media? What the Data Actually Says',
    excerpt: 'Large studies of millions of posts point to a clear answer for small businesses: about three posts a week, every week. Here is what the data shows, how it differs by platform, and why consistency matters more than volume.',

    // SEO Metadata
    seo: {
      metaTitle: 'How Often Should a Small Business Post on Social Media? | Revfer',
      metaDescription: 'What studies of millions of posts say about posting frequency for small businesses: about three posts a week, every week. Platform-by-platform guidance and why consistency beats volume.',
      focusKeyword: 'how often should a small business post on social media',
      keywords: [
        'how often should a small business post on social media',
        'social media posting frequency',
        'how often to post on Instagram for business',
        'how often to post on LinkedIn',
        'how often to post on Facebook business page',
        'social media consistency',
        'small business posting schedule',
        'Google Business Profile posts'
      ],
      canonicalUrl: 'https://www.revfer.com/blog/how-often-small-business-post-social-media',
      ogImage: 'https://revfer-platform-photos-prod.s3.us-east-1.amazonaws.com/blogs/social+media+posting+frequency+hero.png',
      twitterCard: 'summary_large_image'
    },

    // FAQ — rendered visibly at the end of the post and as FAQPage schema
    faq: [
      {
        question: 'How often should a small business post on social media?',
        answer: "About three times a week, every week, is a good default. Buffer's 2025 study of about 2.1 million Instagram posts found that 3 to 5 posts a week more than doubled follower growth compared with 1 to 2, and called that range the sweet spot for growth without burning out. If three is too many to sustain, post less often, but every week."
      },
      {
        question: 'Is consistency more important than posting often?',
        answer: "Yes. Buffer's 2025 analysis of more than 100,000 users counted how many weeks out of 26 each user posted. Those who posted in 20 or more weeks got more than five times the engagement per post of those who posted in four weeks or fewer. Posting once every week beats posting ten times in one week and then going quiet."
      },
      {
        question: 'How often should a business post on Instagram?',
        answer: "Three to five times a week. In Buffer's 2025 Instagram study, that range more than doubled follower growth compared with posting once or twice a week and gave about 12% more reach per post. Posting more often helped further, but Buffer describes the returns as diminishing and recommends 3 to 5 as the sustainable range."
      },
      {
        question: 'How often should a business post on LinkedIn?',
        answer: "Two to five times a week. Buffer's 2025 study of more than 2 million LinkedIn posts from over 94,000 accounts found that posting more often improved each post's impressions and engagement, and recommended 2 to 5 posts a week as the sweet spot for sustainable growth."
      },
      {
        question: 'How often should a business post on Facebook?',
        answer: "We have not found a recent large-scale study for Facebook business pages that is as rigorous as Buffer's Instagram and LinkedIn work. A sensible starting point is the same three posts a week, every week. Check your page's insights after a couple of months and adjust."
      },
      {
        question: 'Can a small business post too often?',
        answer: "The real limit is quality and your own time, not the platform. Buffer's data shows that more posts per week kept adding reach, but each step added less. If posting more means rushed, generic posts, or burning out and stopping, it does more harm than good. Pick the highest cadence you can keep up with posts you are happy with."
      },
      {
        question: 'Should a small business post on Google Business Profile?',
        answer: "Yes. Posts on your Google Business Profile appear to people searching for you on Google and Maps, whether or not they follow you anywhere. Google says customers are 70% more likely to visit, and 50% more likely to consider buying from, businesses with a complete Business Profile. A weekly update on offers, events or news keeps the profile current."
      }
    ],

    content: `
      <h2>The Short Answer</h2>

      <p>For most small businesses: <strong>about three posts a week, every week</strong>. If that is more than you can sustain, post less often, but don't skip weeks. That answer comes from two large studies, and the reasoning matters as much as the number.</p>

      <h2>Consistency Comes First</h2>

      <p>In 2025, Buffer analyzed more than 100,000 of its users. Instead of counting posts, it counted how many of the previous 26 weeks each user had posted in at all.</p>

      <ul>
        <li>Users who posted in <strong>20 or more of the 26 weeks</strong> got <strong>more than five times the engagement per post</strong> of users who posted in four weeks or fewer.</li>
        <li>Engagement climbed through the first weeks of steady posting: median engagement per post rose <strong>58% from week 2 to week 3</strong>, another 27% in week 4 and 17% in week 5.</li>
      </ul>

      <p>The lesson is that the habit matters more than the volume. One post every week builds more than a burst of daily posting followed by a month of silence. So the first question isn't "how many posts?" but "how many can I keep up <em>every</em> week?"</p>

      <h2>Then Frequency: Three to Five a Week</h2>

      <p>Buffer's 2025 Instagram study looked at about <strong>2.1 million posts from 102,000 accounts</strong>, comparing each account with itself in weeks when it posted more or less often. Compared with posting once or twice a week, posting <strong>3 to 5 times a week</strong>:</p>

      <ul>
        <li><strong>More than doubled the follower growth rate</strong></li>
        <li>Gave about <strong>12% more reach per post</strong></li>
      </ul>

      <p>Posting even more often kept helping, but Buffer describes the returns as diminishing, and calls 3 to 5 posts a week "likely the sweet spot for achieving both reach and growth without burning out or sacrificing quality." Its own summary is worth remembering: "The best posting schedule is one you can stick to consistently."</p>

      <h2>Platform by Platform</h2>

      <ul>
        <li><strong>Instagram: 3 to 5 posts a week.</strong> This is the Buffer finding above.</li>
        <li><strong>LinkedIn: 2 to 5 posts a week.</strong> Buffer's 2025 study of more than 2 million LinkedIn posts from over 94,000 accounts found that posting more often improved impressions and engagement per post, and recommended 2 to 5 a week as the sustainable sweet spot. It matters most if your customers are other businesses.</li>
        <li><strong>Facebook: start with 3 a week.</strong> We haven't found a recent study of Facebook business pages as rigorous as Buffer's, so use the same default and adjust based on your page's insights.</li>
        <li><strong>Google Business Profile: weekly.</strong> This one is different: your posts appear to people searching for you on Google and Maps, whether or not they follow you. Google says customers are <strong>70% more likely to visit</strong>, and 50% more likely to consider buying from, businesses with a complete Business Profile. A weekly post about an offer, an event or what's new keeps it looking alive.</li>
      </ul>

      <p>You don't need to be everywhere. Two platforms where your customers actually are, posted to consistently, beat five platforms posted to occasionally.</p>

      <h2>What About the Best Time to Post?</h2>

      <p>Timing matters less than consistency, and the right time depends on your audience. Most platforms show business accounts when their followers are online. Use that, post at roughly the same times each week, and check after a month or two which posts did best.</p>

      <h2>Why Most Small Businesses Don't Hit Three a Week</h2>

      <p>The data is clear; the hard part is time. Writing a post by hand, from idea to published, easily takes an hour. Three a week is about 156 hours a year, roughly four working weeks. When the business gets busy, posting is the first thing to stop, and the consistency that makes it work goes with it.</p>

      <p>We compare the realistic options, doing it yourself, a freelancer, a part-time hire or AI, in <a href="/blog/real-cost-social-media-content-creation-small-business">The Real Cost of Social Media Content for a Small Business</a>, and look at where owners lose the most time in <a href="/blog/content-creation-time-value-matrix">The Content Creation Time-Value Matrix</a>.</p>

      <h2>A Simple Plan</h2>

      <ol>
        <li><strong>Choose one or two platforms</strong> where your customers already are, plus your Google Business Profile.</li>
        <li><strong>Commit to a cadence you can keep</strong>: three a week if you can, one if that is what's realistic.</li>
        <li><strong>Plan the week in one sitting.</strong> Draft all of the week's posts at once instead of starting from scratch each time.</li>
        <li><strong>Use what is actually happening</strong>: new products, events, busy days, customer stories. Specific news makes better posts than generic tips.</li>
        <li><strong>Review once a month.</strong> Notice which posts got a response and do more of those. Only increase frequency once the current cadence feels easy.</li>
      </ol>

      <h2>What This Looks Like With Revfer</h2>

      <p>With Revfer, you don't have to work out a schedule at all. Revfer picks the posting frequency and schedule for your Facebook, Instagram, LinkedIn and Google Business Profile, and drafts each post from what it knows about your business and what is going on in it. When you have news, you tell Revfer in your own words and it turns that into posts. Nothing goes out until you have approved it.</p>

      <p style="text-align: center; margin: 2rem 0;">
        <a href="/" style="display: inline-block; padding: 12px 24px; background: linear-gradient(135deg, #0D2B6B 0%, #1565C0 100%); color: white; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 1.1rem;">Post consistently without thinking about it: start free →</a>
      </p>

      <h2>Sources</h2>

      <ol>
        <li>Buffer. (2025). "Consistent posting study": more than 100,000 users; those posting in 20 or more of 26 weeks saw more than 5 times the engagement per post of those posting in 4 or fewer; week-over-week gains of 58%, 27% and 17% in weeks 3 to 5. <a href="https://buffer.com/resources/consistent-posting-study" target="_blank" rel="noopener">buffer.com</a></li>
        <li>Buffer. (2025). "How Often Should You Post on Instagram? What Data From 2 Million Posts Tells Us": 2.1 million posts from 102,000 accounts. <a href="https://buffer.com/resources/how-often-to-post-on-instagram/" target="_blank" rel="noopener">buffer.com</a></li>
        <li>Buffer. (2025). "How Often Should You Post on LinkedIn? Data From 2 Million+ Posts": more than 94,000 accounts. <a href="https://buffer.com/resources/how-often-to-post-on-linkedin/" target="_blank" rel="noopener">buffer.com</a></li>
        <li>Google. "Complete your Business Profile on Google": customers are 70% more likely to visit and 50% more likely to consider purchasing from businesses with a complete Business Profile. <a href="https://support.google.com/business/answer/10515606" target="_blank" rel="noopener">support.google.com</a></li>
      </ol>
    `,

    image: 'https://revfer-platform-photos-prod.s3.us-east-1.amazonaws.com/blogs/social+media+posting+frequency+hero.png',

    author: {
      name: 'Pankaj Aggarwal',
      role: 'Founder, Revfer',
      avatar: 'https://revfer-platform-photos-prod.s3.us-east-1.amazonaws.com/blogs/pankaj.jpeg',
      bio: 'Pankaj Aggarwal is the founder of Revfer. He has spent more than 20 years building enterprise software and IoT systems and holds more than 10 US patents. He writes about how small businesses can put AI to work without having to become AI experts.'
    },

    categories: ['Social Media', 'Marketing Strategy', 'Business Growth'],

    tags: [
      'social media posting frequency',
      'small business social media',
      'Instagram strategy',
      'LinkedIn strategy',
      'content consistency',
      'posting schedule',
      'Google Business Profile'
    ],

    publishedDate: '2025-11-17T09:00:00Z',
    modifiedDate: '2026-10-17T09:00:00Z',  // set to the actual publish day
    readTime: 5
  };

  export default post;